import { Injectable, signal } from '@angular/core';
import { Subscription, timer } from 'rxjs';

/**
 * 4.7 (R29, DCMPDEV-738 / DCM-366): the session-idle clock.
 *
 * The backend (`functions/ParentSession.cfc`) ends a parent session after
 * 30 minutes without a request. This service warns 5 minutes before that
 * deadline, so the warning fires after 25 minutes without API activity.
 *
 * The clock is dependency-free on purpose: the HTTP interceptor resets it
 * (`authInterceptor` → `touch()` on every successful API response) and the
 * idle-warning component resets it again on "Stay signed in" (which also
 * pings whoami). It must NOT inject AuthService — the first HTTP request of
 * the app (the boot whoami) fires from AuthService's constructor, so a
 * service resolved from inside an interceptor can never depend back on it
 * (instantiation cycle).
 */
export const SESSION_IDLE_WARNING_MS = 25 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class SessionIdleService {
  /** True once the idle window passed with no successful API activity. */
  readonly warning = signal(false);

  private timerSub: Subscription | null = null;

  /**
   * Reset the idle clock and hide the warning. Called by the interceptor on
   * every successful API response and by the "Stay signed in" button.
   */
  touch(): void {
    this.warning.set(false);
    this.timerSub?.unsubscribe();
    this.timerSub = timer(SESSION_IDLE_WARNING_MS).subscribe(() =>
      this.warning.set(true),
    );
  }
}